import * as React from 'react';

import {useQuery, useMutation} from '@apollo/react-hooks';

import {EmailSequenceProgression} from 'shells/sequence-progression/email-sequence-progression';

import type {
    CancelDripSequenceForEntity as CancelDripSequenceForEntityMutation,
    CancelDripSequenceForEntityVariables as CancelDripSequenceForEntityMutationVariables,
    GetLeadPrimaryContactDripSequenceProgressions as GetLeadPrimaryContactDripSequenceProgressionsQuery,
    GetLeadPrimaryContactDripSequenceProgressionsVariables as GetLeadPrimaryContactDripSequenceProgressionsQueryVariables,
} from 'nutshell-graphql-types';

import CANCEL_DRIP_PROGRESSION from '../../../email-marketing/drip-sequences/recipient-modal/graphql/mutations/cancel-drip-sequence-for-entity.graphql';
import {MessageBodyPreviewModal} from '../../../email-marketing/drip-sequences/recipient-modal/message-body-preview-modal';

import GET_LEAD_DRIP_SEQUENCE_PROGRESSIONS from './graphql/queries/get-lead-drip-sequence-progressions.graphql';

import styles from './lead-drip-sequence-progression-section.module.css';

type Props = {
    leadId: string;
};

export function LeadDripSequenceProgressionSection(props: Props) {
    const [editionToShow, setEditionToShow] = React.useState<
        | {
              templateId: string;
              entityId: string;
          }
        | null
        | undefined
    >(undefined);

    const [cancelDripProgression] = useMutation<
        CancelDripSequenceForEntityMutation,
        CancelDripSequenceForEntityMutationVariables
    >(CANCEL_DRIP_PROGRESSION);

    const {data, refetch} = useQuery<
        GetLeadPrimaryContactDripSequenceProgressionsQuery,
        GetLeadPrimaryContactDripSequenceProgressionsQueryVariables
    >(GET_LEAD_DRIP_SEQUENCE_PROGRESSIONS, {
        variables: {leadId: props.leadId},
        notifyOnNetworkStatusChange: true,
    });

    // @ts-expect-error
    const emailSequences = [];
    if (data && data.lead && data.lead.contacts) {
        data.lead.contacts.edges.forEach((contact) =>
            contact.node.dripProgressions.forEach((sequence) => {
                if (sequence.nextEdition && sequence.nextEditionSendTime && sequence.campaign) {
                    emailSequences.push({
                        id: sequence.id,
                        name: sequence.campaign.name,
                        htmlUrlPath: sequence.campaign.htmlUrlPath,
                        length: sequence.campaign.editions.totalCount,
                        totalSent: sequence.numEditionsSent,
                        sendTime: sequence.nextEditionSendTime,
                        nextEdition: sequence.nextEdition
                            ? {
                                  ...sequence.nextEdition,
                                  emailTemplate: sequence.nextEdition.emailTemplate
                                      ? {
                                            id: sequence.nextEdition.emailTemplate.id,
                                        }
                                      : null,
                              }
                            : null,
                        entity: {
                            id: contact.node.id,
                            name: contact.node.name,
                            htmlUrl: contact.node.htmlUrl,
                        },
                        isPaused: Boolean(sequence.campaign.status === 'PAUSED'),
                    });
                }
            })
        );
    }

    return (
        <>
            <div className={styles.container}>
                <EmailSequenceProgression
                    label='Drip sequences'
                    isDripSequence={true}
                    // @ts-expect-error
                    emailSequences={emailSequences}
                    entityType='lead'
                    showPreviewModal={(emailTemplateId, entityId) => {
                        setEditionToShow({templateId: emailTemplateId, entityId: entityId});
                    }}
                    onCancelSequenceProgression={(dripProgressionId: string) =>
                        cancelDripProgression({
                            variables: {
                                dripProgressionDeleteInput: {
                                    dripProgressionId,
                                },
                            },
                        }).then(() => refetch())
                    }
                />
            </div>
            {editionToShow ? (
                <MessageBodyPreviewModal
                    onClose={() => {
                        setEditionToShow(undefined);
                    }}
                    emailTemplateId={editionToShow.templateId}
                    entityId={editionToShow.entityId}
                />
            ) : undefined}
        </>
    );
}
